import { setMilliseconds, sub } from 'date-fns';
import { createWithEqualityFn as create } from 'zustand/traditional';

import {
  filtersForGroupType,
  receivingClientFilterKey,
  receivingGroupFilterKey,
} from 'components/insights/careTransitions/filters';
import { DATES, END_DATE, PREDECESSOR_GROUP, START_DATE } from 'constants/filterKeysConstants';
import GroupType from 'models/GroupType';

import { FilterOption, FilterState } from './types';

const now = setMilliseconds(new Date(), 0);
const thirtyDaysAgo = sub(now, { days: 30 });

export type BusinessDevelopmentFilterState = FilterState<FilterOption[] | string | Date>;

export type ViewBy = 'referralSource' | 'payerSource';
export type TablePivot = 'patientVolume' | 'discharges' | 'referrals';

export type BusinessDevelopmentSlideoutContext = {
  entityId: string;
  entityName: string;
};

type Actions = {
  setSelectedGroupType: (groupType: GroupType | undefined) => void;
  setPredecessorGroupTypeId: (id: string | undefined) => void;
  setSelectedFilters: (filters: BusinessDevelopmentFilterState) => void;
  resetFilters: () => void;
  setExpanded: (expanded: boolean) => void;
  setViewBy: (viewBy: ViewBy) => void;
  setTablePivot: (tablePivot: TablePivot) => void;
  openSlideout: (entityId: string, entityName: string) => void;
  closeSlideout: () => void;
};

type State = {
  selectedGroupType: GroupType | undefined;
  predecessorGroupTypeId: string | undefined;
  filters: BusinessDevelopmentFilterState;
  expanded: boolean;
  viewBy: ViewBy;
  tablePivot: TablePivot;
  slideoutContext: BusinessDevelopmentSlideoutContext | null;
  actions: Actions;
};

export const useBusinessDevelopmentStore = create<State>((set) => ({
  selectedGroupType: undefined,
  predecessorGroupTypeId: undefined,
  expanded: false,
  viewBy: 'referralSource',
  tablePivot: 'patientVolume',
  slideoutContext: null,
  filters: {
    [START_DATE]: thirtyDaysAgo,
    [END_DATE]: now,
  },
  actions: {
    setSelectedGroupType: (groupType) => set({ selectedGroupType: groupType }),
    setPredecessorGroupTypeId: (id) => set({ predecessorGroupTypeId: id }),
    setSelectedFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
    resetFilters: () =>
      set({
        filters: {
          [START_DATE]: thirtyDaysAgo,
          [END_DATE]: now,
        },
      }),
    setExpanded: (expanded) => set({ expanded }),
    setViewBy: (viewBy) => set({ viewBy }),
    setTablePivot: (tablePivot) => set({ tablePivot }),
    openSlideout: (entityId, entityName) => set({ slideoutContext: { entityId, entityName } }),
    closeSlideout: () => set({ slideoutContext: null }),
  },
}));

export const useBusinessDevelopmentActions = () => useBusinessDevelopmentStore((state) => state.actions);

export const getBusinessDevelopmentAnalyticsFilters = ({
  filters,
  selectedGroupType,
  predecessorGroupTypeId,
}: State) => {
  if (!selectedGroupType || !predecessorGroupTypeId) return null;

  const receivingClientKey = receivingClientFilterKey(selectedGroupType);
  const receivingGroupKey = receivingGroupFilterKey(selectedGroupType);
  const activeFilters = filtersForGroupType(filters, selectedGroupType, PREDECESSOR_GROUP);

  const {
    [START_DATE]: start,
    [END_DATE]: end,
    [receivingClientKey]: providerClient,
    [receivingGroupKey]: group,
    ...rest
  } = activeFilters;

  return {
    ...rest,
    [DATES]: [{ start, end }],
    providerClient,
    group,
    predecessorGroupType: predecessorGroupTypeId,
    groupType: selectedGroupType.id,
  };
};
